import { Grid, IconButton, TextField, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import moment from 'moment/moment';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { Check, Edit, Email } from '@mui/icons-material';
import { getEmployeeNames } from '../../../CalendarUtils';
import { WorkshopCircle } from '../../../../common/ui/WorkshopCircle';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { AccountData, EnrichedEventData, EventService, OrderData } from '../../../../api';
import { useLanguage } from '../../../../common/GeneralUtils';
import { useCalendar } from '../../../CalendarContext/CalendarContext';
import { getOrderPage } from '../../../../common/AccountUtils';
import { getDateFormat, getTimeFormat } from '../../../../common/getDateAndTimeFormat';
import { useEvents } from '../../../swr/useEvents';

export const calculateTotalCheckedInPersons = (orderList: OrderData[]) => {
  return isEmpty(orderList)
    ? 0
    : orderList.reduce((totalPersons, item) => {
        if (item.checkin && item.checkin.persons) {
          return totalPersons + item.checkin.persons;
        }
        return totalPersons;
      }, 0);
};
type EventBoxDialogInfoHeaderProps = {
  event: EnrichedEventData;
  ordersList: OrderData[];
  isLoading: boolean;
};
export const EventBoxDialogInfoHeader = ({ event, ordersList, isLoading }: EventBoxDialogInfoHeaderProps) => {
  const { users } = useCalendar();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);
  const { longDateFormat } = getDateFormat(account);
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [internalComment, setInternalComment] = useState(event.comment || '');
  const [sendToManager, setSendToManager] = useState(false);
  const { startDate, endDate } = useCalendar();
  const { reFetch: refetchEvents } = useEvents({ startDate, endDate });
  const handleSaveComment = async () => {
    try {
      await EventService.updateEventComment({
        event_id: event.id,
        comment: internalComment,
        send_to_manager: sendToManager,
      });
      void refetchEvents();
      setIsEditingComment(false);
    } catch (error) {
      console.error('Failed to update comment:', error);
    }
  };
  const language = useLanguage();
  const orderPage = getOrderPage(account, event.order_page_id);
  const { t } = useTranslation();
  // global setting doesnt seem to obe propely updated and refreshed on lang change
  moment.locale(language);
  const orderPageCustomData = orderPage.custom_details;
  const customUsages = event.custom_ticket_usages;
  const customUsedTickets = customUsages?.reduce((total, usage) => total + usage.used_tickets, 0) || 0;
  const usedTickets = event.used_tickets + customUsedTickets;
  const accountLocations = account.locations;
  return (
    <Grid container alignItems="flex-start" flexDirection="column" mx={1}>
      <Grid container mb={1} alignItems="center" gap={1}>
        <WorkshopCircle account={account} language={language} orderPageId={orderPage.id} />
        <Typography variant="h5">{orderPage.label[language]}</Typography>
      </Grid>
      {accountLocations.length > 1 && (
        <Typography variant="body1" color="#555555" fontWeight={700}>
          {accountLocations.find((x) => x.id === event.location_id)?.name?.[language]}
        </Typography>
      )}
      <Typography variant="body1" color="#555555" fontWeight={700}>
        <Grid container>
          {moment(event.date).format(longDateFormat)} ⋅ {moment.unix(event.time).utc().format(timeFormat)}-
          {moment
            .unix(event.time + event.duration)
            .utc()
            .format(timeFormat)}
        </Grid>
      </Typography>
      <Grid container>
        <Grid container>
          <Grid flexDirection="column" item>
            <Typography variant="body1" color="#555555">
              {`${t('calendar.event_popper.participants')}:`} {usedTickets}/{event.tickets}
            </Typography>
            {customUsages &&
              customUsages?.length > 0 &&
              customUsages.map((customTicketUsage) => (
                <Typography variant="body1" color="#555555">
                  {`${orderPageCustomData.find((x: any) => x.name === customTicketUsage.name)?.label.en} ${t(
                    'calendar.event_popper.participants',
                  )}: `}
                  {customTicketUsage.used_tickets}/{customTicketUsage.tickets}
                </Typography>
              ))}

            <Typography variant="body1" color="#555555">
              {`${t('calendar.event_popper.numOfOrders')}:`} {isLoading ? 0 : ordersList.length}
            </Typography>
            <Typography variant="body1" color="#555555">
              {`${t('calendar.event_popper.numOfFreeTickets')}: `}
              {isLoading ? 0 : ordersList.filter((x) => x.price === 0).reduce((sum, item) => sum + item.persons, 0)}
            </Typography>

            {event.employee_ids.length > 0 && (
              <Grid item container gap={1}>
                <Typography variant="body1" color="#555555">
                  {t('calendar.event_popper.employees')}
                </Typography>
                {getEmployeeNames(event.employee_ids, users).map((x) => (
                  <Grid item key={x}>
                    <Typography variant="body1" color="#555555">
                      {x}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            )}
            <Grid item>
              <Typography variant="body1" color="#555555">
                {t('calendar.event_popper.total_checkeding', {
                  total: isLoading ? 0 : calculateTotalCheckedInPersons(ordersList),
                })}
              </Typography>
            </Grid>
          </Grid>
          <Grid style={{ marginInlineStart: '32px' }}>
            <Gauge
              width={100}
              height={100}
              value={usedTickets}
              startAngle={-90}
              endAngle={90}
              valueMax={event.tickets}
              text={({ value, valueMax }) => `${value} / ${valueMax}`}
              sx={() => ({
                [`& .${gaugeClasses.valueArc}`]: {
                  fill: '#4ABD00',
                },
              })}
            />
          </Grid>
        </Grid>
        <Grid item container alignItems="center" gap={1} mt={1}>
          {isEditingComment ? (
            <Grid container direction="column" gap={1}>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={internalComment}
                onChange={(e) => setInternalComment(e.target.value)}
                placeholder={t('calendar.event_popper.commentPlaceholder')}
                size="small"
              />
              <Grid container gap={1}>
                <IconButton
                  size="small"
                  onClick={() => handleSaveComment()}
                  color={sendToManager ? 'primary' : 'default'}
                  onMouseEnter={() => setSendToManager(true)}
                  onMouseLeave={() => setSendToManager(false)}>
                  <Tooltip title={t('calendar.event_popper.sendToManager')}>
                    <Email />
                  </Tooltip>
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSendToManager(false);
                    void handleSaveComment();
                  }}>
                  <Tooltip title={t('calendar.event_popper.saveComment')}>
                    <Check />
                  </Tooltip>
                </IconButton>
              </Grid>
            </Grid>
          ) : (
            <Grid container alignItems="center" gap={1}>
              <Typography variant="body1" color="#555555">
                {internalComment || t('calendar.event_popper.noComment')}
              </Typography>
              <IconButton size="small" onClick={() => setIsEditingComment(true)}>
                <Edit fontSize="small" />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
